import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@shadowhealth/oidc-login";
import { enqueueSnackbar } from "notistack";
import callApi from "../helpers/callApi";
import useDebouncedValue from "../hooks/useDebouncedValue";
import RunSetupPanel from "../components/RunSetupPanel";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import DeleteIcon from "@mui/icons-material/Delete";
import { DataGrid } from "@mui/x-data-grid";

const PRESET_OPTIONS = [
  {
    value: "convoengine_plus",
    label: "ConvoEngine Plus",
    description: "Sentence-embedding and term-embedding matching together",
  },
  {
    value: "cws",
    label: "CWS",
    description: "Classic CWS matching",
  },
  {
    value: "semantic_only",
    label: "Semantic Only",
    description: "Sentence-embedding similarity only, no keyword matching",
  },
  {
    value: "lexical_vector_only",
    label: "Lexical Vector Only",
    description: "Term-embedding keyword matching only, no sentence similarity",
  },
];

const EvaluationsPage = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [tags, setTags] = useState([]);
  const [total, setTotal] = useState(0);
  const [activeRuns, setActiveRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search.trim());
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  // MUI X DataGrid v8: rowSelectionModel = { type: 'include'|'exclude', ids: Set<id> }
  const [selectionModel, setSelectionModel] = useState({ type: "include", ids: new Set() });
  const [goldenSets, setGoldenSets] = useState([]);
  const [goldenSetsLoading, setGoldenSetsLoading] = useState(false);
  const [goldenSetSearch, setGoldenSetSearch] = useState("");
  const debouncedGoldenSetSearch = useDebouncedValue(goldenSetSearch.trim());
  const [deleteTag, setDeleteTag] = useState(null); // tag string to confirm deletion
  const [deleting, setDeleting] = useState(false);

  // Search/pagination run server-side (GET /eval/tags?q=&limit=&offset=) — the
  // list can grow past what's reasonable to load in one response.
  const fetchTags = useCallback(async () => {
    setLoading(true);
    try {
      const { page, pageSize } = paginationModel;
      const params = new URLSearchParams({
        limit: pageSize,
        offset: page * pageSize,
      });
      if (debouncedSearch) {
        params.set("q", debouncedSearch);
      }
      const tagsResp = await callApi(`/eval/tags?${params.toString()}`, "GET", undefined, auth.user?.access_token);
      setTags(tagsResp.tags || []);
      setTotal(tagsResp.total || 0);
    } catch (err) {
      enqueueSnackbar(err.message, { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [paginationModel, debouncedSearch, auth.user?.access_token]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  // A new search term invalidates whatever page we were on.
  useEffect(() => {
    setPaginationModel((m) => (m.page === 0 ? m : { ...m, page: 0 }));
  }, [debouncedSearch]);

  useEffect(() => {
    // Active runs are a small, transient set (only queued/running) — always
    // fetched in full, independent of the tags list's own pagination/search.
    let cancelled = false;
    const fetchActiveRuns = async () => {
      try {
        const runsResp = await callApi("/eval/runs", "GET", undefined, auth.user?.access_token);
        if (cancelled) return;
        setActiveRuns(runsResp || []);
        if (!runsResp?.length) fetchTags(); // a run may have just completed — refresh current page
      } catch {
        // Transient poll failures are expected; the next 5s tick retries.
      }
    };
    fetchActiveRuns();
    const interval = setInterval(fetchActiveRuns, 5000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // Re-subscribe when fetchTags changes (pagination/search) so the refresh-on-
    // completion call above never closes over a stale page/query.
  }, [auth.user?.access_token, fetchTags]);

  // Golden sets for setup step 1 (GET /eval/golden-sets?q=, PROG-66951). Search runs
  // on the server; GoldenSetPicker also filters what is loaded as the user types.
  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams({ limit: 25, offset: 0 });
    if (debouncedGoldenSetSearch) params.set("q", debouncedGoldenSetSearch);
    setGoldenSetsLoading(true);
    callApi(`/eval/golden-sets?${params.toString()}`, "GET", undefined, auth.user?.access_token)
      .then((resp) => {
        if (!cancelled) setGoldenSets(resp.items || []);
      })
      .catch((err) => {
        if (!cancelled) enqueueSnackbar(err.message, { variant: "error" });
      })
      .finally(() => {
        if (!cancelled) setGoldenSetsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedGoldenSetSearch, auth.user?.access_token]);

  // Returns the stored golden set, or null after showing the error (the form keeps its entries).
  const handleAddGoldenSet = async ({ name, csvContent, csvFilename }) => {
    try {
      const body = { name, csv_content: csvContent };
      if (csvFilename) body.csv_filename = csvFilename;
      const added = await callApi("/eval/golden-sets", "POST", body, auth.user?.access_token);
      setGoldenSets((sets) => [added, ...sets.filter((g) => g.id !== added.id)]);
      enqueueSnackbar(`Golden set "${added.name}" added`, { variant: "success" });
      return added;
    } catch (err) {
      enqueueSnackbar(err.message || "Failed to add golden set", { variant: "error" });
      return null;
    }
  };

  const selectedCount = selectionModel.ids.size;

  const handleDeleteConfirm = async () => {
    if (!deleteTag) return;
    setDeleting(true);
    try {
      await callApi(`/eval/tags/${encodeURIComponent(deleteTag)}`, "DELETE", undefined, auth.user?.access_token);
      enqueueSnackbar(`Evaluation "${deleteTag}" deleted`, { variant: "success" });
      setDeleteTag(null);
      fetchTags();
    } catch (err) {
      enqueueSnackbar(err.message || "Failed to delete evaluation", { variant: "error" });
    } finally {
      setDeleting(false);
    }
  };

  const handleCompare = () => {
    const selectedTags = rows.filter((r) => selectionModel.ids.has(r.id)).map((r) => r.tag);
    const params = selectedTags.map((t) => `tags=${encodeURIComponent(t)}`).join("&");
    navigate(`/evaluations/compare?${params}`);
  };

  // Returns true when the run started, so setup can close; false keeps setup open.
  const handleStartRun = async ({ tag, production, test, goldenSet }) => {
    const body = {
      tag,
      presets: [production, test],
      production_preset: production,
      test_preset: test,
      golden_set_id: goldenSet.id,
    };
    const email = auth.user?.profile?.email;
    if (email) body.created_by = email;
    try {
      await callApi("/eval/runs", "POST", body, auth.user?.access_token);
      enqueueSnackbar(`Run "${tag}" started. Its results will be on the run page.`, { variant: "success" });
      fetchTags();
      return true;
    } catch (err) {
      enqueueSnackbar(err.message || "Failed to start the run", { variant: "error" });
      return false;
    }
  };

  const columns = [
    {
      field: "tag",
      headerName: "Evaluation name",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Button
          variant="text"
          size="small"
          onClick={() => navigate(`/evaluations/${encodeURIComponent(params.row.tag)}`)}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          {params.row.tag}
        </Button>
      ),
      display: "flex",
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      renderCell: (params) => {
        const STATUS = {
          queued: { label: "Processing…", color: "warning" },
          running: { label: "Processing…", color: "warning" },
          done: { label: "Completed", color: "success" },
          failed: { label: "Failed", color: "error" },
        };
        const s = STATUS[params.value];
        return s ? <Chip label={s.label} size="small" color={s.color} /> : null;
      },
      display: "flex",
    },
    { field: "created_by", headerName: "Created by", flex: 1, minWidth: 160, valueFormatter: (v) => v || "—" },
    {
      field: "latest",
      headerName: "Last updated",
      flex: 1,
      minWidth: 180,
      valueFormatter: (value) => (value ? new Date(value).toLocaleString() : "—"),
    },
    {
      field: "actions",
      headerName: "",
      width: 56,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Tooltip title="Delete evaluation">
          <IconButton
            size="small"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              setDeleteTag(params.row.tag);
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
      display: "flex",
    },
  ];

  // Active runs as rows (deduped by tag — keep latest)
  const activeByTag = {};
  activeRuns.forEach((r) => {
    if (!activeByTag[r.tag] || new Date(r.created_at) > new Date(activeByTag[r.tag].created_at)) {
      activeByTag[r.tag] = r;
    }
  });

  const tagRows = tags.map((t, i) => ({
    id: `tag-${i}`,
    tag: t.tag,
    status: activeByTag[t.tag]?.status ?? t.latest_run_status ?? "done",
    created_by: activeByTag[t.tag]?.created_by ?? t.created_by ?? null,
    runs: t.runs,
    latest: t.latest,
  }));

  // Merge: active runs not yet on the current tags page appear pinned at the
  // top. `tags` is already search+pagination filtered server-side, so only
  // this small, separately-fetched active-runs set needs a client-side text
  // filter (it isn't part of the paginated/search query).
  const searchLower = debouncedSearch.toLowerCase();
  const activeRows = Object.values(activeByTag)
    .filter((r) => !tags.some((t) => t.tag === r.tag))
    .filter((r) => !searchLower || r.tag.toLowerCase().includes(searchLower))
    .map((r) => ({
      id: `run-${r.run_id}`,
      tag: r.tag,
      status: r.status,
      created_by: r.created_by ?? null,
      runs: 0,
      latest: r.created_at,
    }));

  const rows = [...activeRows, ...tagRows];

  return (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="column" spacing={0.5}>
              <Typography variant="h5" component="h1">
                Evaluations
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Compare how engine versions answer the same golden set questions before a release
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              {selectedCount >= 2 && (
                <Button variant="outlined" startIcon={<CompareArrowsIcon />} onClick={handleCompare}>
                  Compare ({selectedCount})
                </Button>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <RunSetupPanel
        presetOptions={PRESET_OPTIONS}
        goldenSets={goldenSets}
        goldenSetsLoading={goldenSetsLoading}
        onSearchGoldenSets={setGoldenSetSearch}
        onAddGoldenSet={handleAddGoldenSet}
        existingTags={tags.map((t) => t.tag)}
        onStart={handleStartRun}
      />

      <Card>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <TextField
              size="small"
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 260 }}
            />
            {selectedCount === 1 && (
              <Typography variant="body2" color="text.secondary">
                Select one more run to compare
              </Typography>
            )}
          </Stack>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            autoHeight
            density="comfortable"
            checkboxSelection
            onRowSelectionModelChange={(model) => setSelectionModel(model)}
            paginationMode="server"
            rowCount={total}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50]}
            sx={{ border: 0 }}
            localeText={{ noRowsLabel: "No eval tags yet" }}
          />
        </CardContent>
      </Card>

      <Dialog open={!!deleteTag} onClose={() => !deleting && setDeleteTag(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete evaluation?</DialogTitle>
        <DialogContent>
          <Typography>
            This will permanently remove all batches and results for{" "}
            <Box component="span" sx={{ fontWeight: 700, fontFamily: "monospace" }}>
              {deleteTag}
            </Box>
            . This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTag(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={deleting}>
            {deleting ? <CircularProgress size={18} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default EvaluationsPage;
